import { FunctionComponent, useEffect } from "react";
import {
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    SimpleGrid,
    Textarea,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { AddressBookEntry, AddressBookEntryInput, useCreateAddressBookEntry, useStates, useUpdateAddressBookEntry } from "../../api";

export interface AddressBookEditorModalProps {
    entry?: AddressBookEntry;
    isOpen: boolean;
    onClose: () => void;
}

const toInput = (entry?: AddressBookEntry): AddressBookEntryInput => ({
    name: entry?.name ?? "",
    displayName: entry?.displayName ?? "",
    description: entry?.description ?? "",
    phone: entry?.phone ?? "",
    address: {
        street1: entry?.address?.street1 ?? "",
        street2: entry?.address?.street2 ?? "",
        city: entry?.address?.city ?? "",
        county: entry?.address?.county ?? "",
        state: entry?.address?.state ?? "",
        zipCode: entry?.address?.zipCode ?? "",
    },
});

export const AddressBookEditorModal: FunctionComponent<AddressBookEditorModalProps> = ({ entry, isOpen, onClose }) => {
    const form = useForm<AddressBookEntryInput>({ defaultValues: toInput(entry) });
    const createEntry = useCreateAddressBookEntry();
    const updateEntry = useUpdateAddressBookEntry();
    const states = useStates();
    const isSaving = createEntry.isLoading || updateEntry.isLoading;

    useEffect(() => {
        form.reset(toInput(entry));
    }, [entry, form, isOpen]);

    const close = () => {
        if (!isSaving) {
            form.reset(toInput(entry));
            onClose();
        }
    };

    const save = async (data: AddressBookEntryInput) => {
        try {
            if (entry) {
                await updateEntry.mutateAsync({ id: entry.id, entry: data });
            } else {
                await createEntry.mutateAsync(data);
            }
            onClose();
        } catch {
            return;
        }
    };

    const errors = form.formState.errors;
    return (
        <Modal isOpen={isOpen} onClose={close} size="xl">
            <ModalOverlay />
            <ModalContent>
                <form onSubmit={form.handleSubmit(save)}>
                    <ModalHeader>{entry ? "Edit address book entry" : "Create address book entry"}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Input type="hidden" {...form.register("address.county")} />
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <FormControl isRequired isInvalid={!!errors.displayName}>
                                <FormLabel>Display name</FormLabel>
                                <Input maxLength={100} {...form.register("displayName", { required: "Display name is required" })} />
                                <FormErrorMessage>{errors.displayName?.message}</FormErrorMessage>
                            </FormControl>
                            <FormControl isRequired isInvalid={!!errors.name}>
                                <FormLabel>Contact / envelope name</FormLabel>
                                <Input maxLength={100} {...form.register("name", { required: "Contact or envelope name is required" })} />
                                <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
                            </FormControl>
                            <FormControl gridColumn={{ md: "span 2" }}>
                                <FormLabel>Description</FormLabel>
                                <Textarea maxLength={4000} rows={2} {...form.register("description")} />
                            </FormControl>
                            <FormControl gridColumn={{ md: "span 2" }}>
                                <FormLabel>Phone</FormLabel>
                                <Input maxLength={55} placeholder="(555) 000-0000" {...form.register("phone")} />
                            </FormControl>
                            <FormControl gridColumn={{ md: "span 2" }} isRequired isInvalid={!!errors.address?.street1}>
                                <FormLabel>Street 1</FormLabel>
                                <Input maxLength={255} {...form.register("address.street1", { required: "Street 1 is required" })} />
                                <FormErrorMessage>{errors.address?.street1?.message}</FormErrorMessage>
                            </FormControl>
                            <FormControl gridColumn={{ md: "span 2" }}>
                                <FormLabel>Street 2</FormLabel>
                                <Input maxLength={255} {...form.register("address.street2")} />
                            </FormControl>
                            <FormControl isRequired isInvalid={!!errors.address?.city}>
                                <FormLabel>City</FormLabel>
                                <Input maxLength={255} {...form.register("address.city", { required: "City is required" })} />
                                <FormErrorMessage>{errors.address?.city?.message}</FormErrorMessage>
                            </FormControl>
                            <FormControl isRequired isInvalid={!!errors.address?.state}>
                                <FormLabel>State</FormLabel>
                                <Select placeholder="Select state" {...form.register("address.state", { required: "State is required" })}>
                                    {(states.data ?? []).map(state => (
                                        <option key={state.code} value={state.code}>
                                            {state.name}
                                        </option>
                                    ))}
                                </Select>
                                <FormErrorMessage>{errors.address?.state?.message}</FormErrorMessage>
                            </FormControl>
                            <FormControl isRequired isInvalid={!!errors.address?.zipCode}>
                                <FormLabel>ZIP code</FormLabel>
                                <Input maxLength={10} {...form.register("address.zipCode", { required: "ZIP code is required" })} />
                                <FormErrorMessage>{errors.address?.zipCode?.message}</FormErrorMessage>
                            </FormControl>
                        </SimpleGrid>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={close} isDisabled={isSaving}>
                            Cancel
                        </Button>
                        <Button colorScheme="blue" type="submit" isLoading={isSaving}>
                            {entry ? "Save" : "Create"}
                        </Button>
                    </ModalFooter>
                </form>
            </ModalContent>
        </Modal>
    );
};
